import type { AppliedCredit, Invoice } from '@/types/invoice'
import { InlineField } from '@/components/common'
import { formatCurrency, formatDate } from './invoiceUtils'

interface Props {
  invoice: Invoice
  applied: AppliedCredit[]
  onSave: (field: string, value: string | number | null) => Promise<unknown>
}

/**
 * Betraege. Netto/MwSt/Brutto sind einheitliche, inline editierbare Zeilen; die
 * grosse Zahl traegt die Kopfleiste. Der Restbetrags-Block erscheint nur, wenn er
 * vom Brutto abweicht — die vier Faelle dahinter stammen aus dem alten Modal und
 * sind fachlich verschieden: bezahlte Teilrechnungen einer Mahnung, verrechnete
 * Gutschriften bzw. Teilzahlungen, eine bereits verbrauchte Gutschrift.
 */
export default function InvoiceAmountsPanel({ invoice, applied, onSave }: Props) {
  const isReminder = invoice.type === 'reminder'

  const linked = invoice.linkedInvoices || []
  const paidLinked = isReminder ? linked.filter((i) => i.status === 'paid') : []
  const paidLinkedSum = paidLinked.reduce((s, i) => s + (i.gross_amount || 0), 0)

  const appliedSum = applied.reduce((s, a) => s + (a.applied_amount || 0), 0)
  const partialPaid = invoice.status !== 'paid' ? (invoice.paid_amount || 0) : 0

  let rest: { amount: number; was: number | null; caption: string } | null = null

  if (paidLinked.length > 0) {
    rest = {
      amount: Math.round(((invoice.gross_amount || 0) - paidLinkedSum) * 100) / 100,
      was: invoice.gross_amount,
      caption: `${paidLinked.length} von ${linked.length} Rechnungen bezahlt`,
    }
  } else if (appliedSum > 0 || partialPaid > 0) {
    const parts: string[] = []
    if (appliedSum > 0) parts.push(`abzgl. Gutschrift ${formatCurrency(appliedSum)}`)
    if (partialPaid > 0) parts.push(`Teilzahlung ${formatCurrency(partialPaid)}`)
    rest = {
      amount: Math.round(((invoice.gross_amount || 0) - appliedSum - partialPaid) * 100) / 100,
      was: invoice.gross_amount,
      caption: parts.join(' · '),
    }
  } else if ((invoice.credit_used_amount || 0) > 0) {
    rest = {
      amount: invoice.gross_amount || 0,
      was: invoice.credit_original_amount ?? null,
      caption: 'nach Verrechnung',
    }
  }

  const skontoExpired = !!invoice.skonto_date && new Date(invoice.skonto_date) < new Date()
  const skontoPayable =
    invoice.gross_amount != null && invoice.skonto_amount != null
      ? invoice.gross_amount - invoice.skonto_amount
      : null

  return (
    <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <InlineField label="Netto" field="net_amount" value={invoice.net_amount}
        type="currency" display={formatCurrency} onSave={onSave} />
      <InlineField label="MwSt." field="vat_amount" value={invoice.vat_amount}
        type="currency" display={formatCurrency} onSave={onSave} />
      <InlineField label="Brutto" field="gross_amount" value={invoice.gross_amount}
        type="currency" display={formatCurrency} onSave={onSave} />

      {rest && (
        <div className="flex items-baseline justify-between gap-3 border-t border-gray-100 bg-emerald-50/60 px-3 py-2">
          <span className="text-[10px] uppercase tracking-wider text-emerald-700">Offener Restbetrag</span>
          <span className="text-right">
            {rest.was != null && (
              <span className="mr-2 text-xs tabular-nums text-gray-400 line-through">{formatCurrency(rest.was)}</span>
            )}
            <b className="text-lg font-bold tabular-nums text-emerald-700">{formatCurrency(rest.amount)}</b>
            <span className="block text-[10px] text-emerald-600">{rest.caption}</span>
          </span>
        </div>
      )}

      {/* Skonto — bei Gutschriften fachlich sinnlos */}
      {invoice.type !== 'credit_note' && (
        <>
          <InlineField label="Skonto %" field="skonto_percent" value={invoice.skonto_percent}
            type="number" onSave={onSave} />
          {/* Die Frist steht auf den meisten Belegen als DATUM ("Bis zum
              09.03.2026 erhalten Sie 3 % Skonto"), nicht als Tagesangabe — und
              sie fuehrt seit #125 die Faellig-Spalte an. Sie muss deshalb direkt
              korrigierbar sein und nicht nur ueber den Umweg der Skontotage. */}
          <InlineField label="Skonto bis" field="skonto_date" value={invoice.skonto_date}
            type="date" display={formatDate} placeholder="Klicken…" onSave={onSave} />
          <InlineField label="Skonto Tage" field="skonto_days" value={invoice.skonto_days}
            type="number" onSave={onSave} />
          {/* Der Abzug war bis #129 eine reine Anzeigezeile — und stand deshalb
              gar nicht da, wenn der Beleg ihn nicht ausdruckt (alle Jäger-
              Rechnungen). Genau dort will die Buchhaltung ihn aber eintragen
              koennen: sie liest ihn vom Beleg ab oder rechnet ihn gegen.
              Ein hier von Hand gesetzter Wert ueberlebt jede weitere Bearbeitung
              — computeSkonto() raeumt ihn nur beim Aendern des SATZES weg, weil
              der Abzug dann bewusst neu bestimmt werden soll. */}
          <InlineField label="Abzug" field="skonto_amount" value={invoice.skonto_amount}
            type="currency" display={formatCurrency} placeholder="Klicken…" onSave={onSave} />
          {invoice.skonto_percent != null && invoice.skonto_amount != null && (
            <p className={`flex flex-wrap items-baseline justify-between gap-x-3 border-t border-gray-100 px-3 py-1.5 text-xs tabular-nums ${
              skontoExpired ? 'text-gray-400 line-through' : 'text-emerald-700'
            }`}>
              {/* "Abzug" steht seit #129 als editierbares Feld direkt darueber —
                  hier bleibt nur, was daraus FOLGT: was zu zahlen ist und bis wann. */}
              <span>{skontoPayable != null ? <>zahlbar {formatCurrency(skontoPayable)}</> : 'Skonto'}</span>
              <span className={skontoExpired ? '' : 'text-gray-500'}>
                {invoice.skonto_date && <>bis {formatDate(invoice.skonto_date)}</>}
                {skontoExpired && ' · abgelaufen'}
              </span>
            </p>
          )}
        </>
      )}

      {/* Nebenforderung bei Mahnungen — die Detailpflege sitzt im ReminderFeesEditor */}
      {isReminder && invoice.fees_amount != null && (
        <p className="flex items-baseline justify-between gap-3 border-t border-gray-100 px-3 py-1.5 text-xs tabular-nums text-orange-700">
          <span>Nebenforderung {formatCurrency(invoice.fees_amount)}</span>
          <span className="text-gray-500">{invoice.fees_source === 'manual' ? 'manuell gepflegt' : 'aus Beleg'}</span>
        </p>
      )}
    </section>
  )
}
